import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Loader2, ArrowRight, CheckCircle2, Download, Usb, Sparkles,
  ShieldCheck, Search, Star, BookOpen,
} from 'lucide-react';
import { bundlesAPI } from '@/services/api';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export default function BundleLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();

  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    bundlesAPI.getBySlug(slug)
      .then((res) => { if (!cancelled) setBundle(res.data); })
      .catch(() => { if (!cancelled) setBundle(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (bundle?.seo_title) document.title = bundle.seo_title;
  }, [bundle]);

  const handleAddToCart = async (variant) => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/bundles/${slug}`);
      return;
    }
    setAdding(true);
    const result = await addToCart('bundle', bundle.id, variant);
    if (result.success) {
      toast.success('Bundle added to cart!');
      navigate('/cart');
    }
    setAdding(false);
  };

  const filteredWebinars = useMemo(() => {
    if (!bundle?.webinars) return [];
    if (!search.trim()) return bundle.webinars;
    const q = search.toLowerCase();
    return bundle.webinars.filter((w) => {
      const speakerNames = (w.speaker_names || []).join(' ').toLowerCase();
      const tags = Array.isArray(w.tags) ? w.tags.join(' ').toLowerCase() : '';
      return (
        (w.title || '').toLowerCase().includes(q) ||
        (w.webinar_code || '').toLowerCase().includes(q) ||
        (w.legacy_webinar_code || '').toLowerCase().includes(q) ||
        (w.description || '').toLowerCase().includes(q) ||
        (w.short_description || '').toLowerCase().includes(q) ||
        speakerNames.includes(q) ||
        tags.includes(q)
      );
    });
  }, [bundle, search]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="min-h-screen bg-slate-50 pt-32 flex flex-col items-center justify-center px-4 text-center">
        <h1 className="font-heading text-3xl font-bold text-navy-700 mb-3">Bundle not found</h1>
        <p className="text-slate-500 mb-6">This bundle may have been moved or is no longer active.</p>
        <Button onClick={() => navigate('/')} className="bg-navy-600 hover:bg-navy-700">
          Back to Home
        </Button>
      </div>
    );
  }

  const downloadSavings = bundle.original_price_download
    ? Math.round((1 - bundle.price_download / bundle.original_price_download) * 100)
    : 0;
  const usbSavings = bundle.original_price_usb
    ? Math.round((1 - bundle.price_usb / bundle.original_price_usb) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-transparent" data-testid="bundle-landing-page">
      {/* ================ HERO ================ */}
      <section className="relative pt-32 pb-16 hero-canvas border-b border-zinc-200 overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <div className="mono-label inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-zinc-200 text-[11px] text-zinc-600 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {bundle.hero_eyebrow || 'Professional Development Bundle'}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.03em] leading-[1.0] mb-6 text-zinc-950"
                data-testid="bundle-hero-title">
              {bundle.title}
            </h1>
            {bundle.subtitle && (
              <p className="text-base sm:text-lg text-zinc-500 mb-10 max-w-3xl leading-relaxed">
                {bundle.subtitle}
              </p>
            )}
            <div className="flex flex-wrap gap-3 mb-12">
              <Button
                size="lg"
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-zinc-950 hover:bg-zinc-800 text-white font-semibold px-7 py-6 rounded-lg"
                data-testid="hero-cta-pricing"
              >
                See Pricing
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('library')?.scrollIntoView({ behavior: 'smooth' })}
                className="border border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50 px-7 py-6 rounded-lg"
              >
                Browse the Library
              </Button>
            </div>
            {/* Stats Strip */}
            {bundle.hero_stats?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-zinc-200">
                {bundle.hero_stats.map((s, i) => (
                  <div key={i} data-testid={`hero-stat-${i}`}>
                    <div className="font-heading text-3xl sm:text-4xl font-bold text-zinc-950">{s.value}</div>
                    <div className="mono-label text-[10px] text-zinc-500 mt-1.5">{s.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ================ ABOUT ================ */}
      {bundle.about_paragraphs?.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container-custom max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-800 mb-3"
                  data-testid="about-heading">
                About This Series
              </h2>
              <div className="w-16 h-1 bg-gold-500 mx-auto rounded-full"></div>
            </div>
            <div className="space-y-5 text-slate-600 text-base leading-relaxed">
              {bundle.about_paragraphs.map((p, i) => (
                <p key={i} data-testid={`about-paragraph-${i}`}>{p}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================ LIBRARY ================ */}
      <section id="library" className="py-20 bg-transparent border-t border-zinc-200">
        <div className="container-custom">
          <div className="max-w-3xl mb-10">
            <div className="text-xs font-semibold tracking-wider uppercase text-gold-600 mb-4">
              Full Session Library
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-800 leading-tight">
              Browse All {bundle.webinar_count} Webinars in This Series
            </h2>
            <p className="text-slate-600 mt-4">Search by keyword, topic, or speaker name.</p>
          </div>

          <div className="mb-6 max-w-xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by title, code, speaker, topic..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-11 py-6 rounded-xl border-slate-300"
                data-testid="library-search-input"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Showing {filteredWebinars.length} of {bundle.webinar_count} webinars
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {filteredWebinars.map((w, i) => (
              <a
                key={w.id}
                href={`/webinars/${w.slug || w.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
                data-testid={`library-webinar-${i}`}
              >
                <Card className="border-slate-200 group-hover:border-navy-400 group-hover:shadow-md transition-all rounded-xl h-full cursor-pointer">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <BookOpen className="w-5 h-5 text-navy-400 flex-shrink-0 mt-0.5" />
                      {w.is_bestseller && (
                        <Badge className="bg-gold-100 text-gold-700 border-gold-300 text-xs">
                          <Star className="w-3 h-3 mr-1 fill-current" /> Best Seller
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-medium text-navy-800 text-sm leading-snug mb-2 line-clamp-2 group-hover:text-navy-600">
                      {w.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      {w.webinar_code && (
                        <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono">
                          {w.webinar_code}
                        </code>
                      )}
                      {(w.speaker_names || []).length > 0 && (
                        <span>· {w.speaker_names.join(', ')}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>

          {filteredWebinars.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No webinars match "{search}".
            </div>
          )}
        </div>
      </section>

      {/* ================ BENEFITS ================ */}
      {bundle.benefits?.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-800 mb-3"
                  data-testid="why-choose-heading">
                Why Choose This Series
              </h2>
              <div className="w-16 h-1 bg-gold-500 mx-auto rounded-full"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bundle.benefits.map((b, i) => (
                <Card key={i} className="border-slate-200 hover:border-gold-400 hover:shadow-lg transition-all rounded-xl"
                      data-testid={`benefit-card-${i}`}>
                  <CardContent className="p-6">
                    <div className="text-4xl mb-4">{b.icon}</div>
                    <h3 className="font-heading text-lg font-semibold text-navy-700 mb-2">{b.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================ PRICING ================ */}
      <section id="pricing" className="py-20 bg-transparent border-t border-zinc-200">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-xs font-semibold tracking-wider uppercase text-gold-600 mb-4">
              Choose Your Format
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-800 leading-tight">
              Get the Entire Series in Your Preferred Format
            </h2>
            <p className="text-slate-600 mt-4">
              All {bundle.webinar_count} recorded webinars — same content, two delivery options.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Download */}
            <Card className="border-2 border-slate-200 rounded-2xl hover:border-navy-400 hover:shadow-2xl transition-all relative overflow-hidden"
                  data-testid="pricing-card-download">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-xl bg-navy-100 flex items-center justify-center">
                    <Download className="w-5 h-5 text-navy-700" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-navy-800">Download Access</h3>
                    <p className="text-xs text-slate-500">Instant digital delivery</p>
                  </div>
                </div>
                <div className="mt-6">
                  {bundle.original_price_download && (
                    <p className="text-sm text-slate-400 line-through">
                      ${bundle.original_price_download.toLocaleString()} individually
                    </p>
                  )}
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-5xl font-bold text-navy-800">
                      ${bundle.price_download.toFixed(0)}
                    </span>
                    {downloadSavings > 0 && (
                      <span className="text-sm font-semibold text-green-600">Save {downloadSavings}%</span>
                    )}
                  </div>
                </div>
                <ul className="space-y-3 mt-8 text-sm text-slate-700">
                  <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" /> All {bundle.webinar_count} recorded webinars</li>
                  <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" /> Instant access after purchase</li>
                  <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" /> Stream or download to any device</li>
                  <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" /> Lifetime access</li>
                </ul>
                <Button
                  size="lg"
                  className="w-full mt-8 bg-navy-600 hover:bg-navy-700 text-white font-semibold py-6 rounded-xl"
                  onClick={() => handleAddToCart('download')}
                  disabled={adding}
                  data-testid="add-bundle-download-btn"
                >
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get Download Access<ArrowRight className="ml-2 w-4 h-4" /></>}
                </Button>
              </CardContent>
            </Card>

            {/* USB */}
            <Card className="border-2 border-gold-400 rounded-2xl hover:shadow-2xl transition-all relative overflow-hidden"
                  data-testid="pricing-card-usb">
              <div className="absolute top-0 right-0 bg-gold-500 text-navy-900 text-xs font-bold px-4 py-1 rounded-bl-lg">
                BEST VALUE
              </div>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-xl bg-gold-100 flex items-center justify-center">
                    <Usb className="w-5 h-5 text-gold-700" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-navy-800">USB Flash Drive</h3>
                    <p className="text-xs text-slate-500">Physical drive + digital access</p>
                  </div>
                </div>
                <div className="mt-6">
                  {bundle.original_price_usb && (
                    <p className="text-sm text-slate-400 line-through">
                      ${bundle.original_price_usb.toLocaleString()} individually
                    </p>
                  )}
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-5xl font-bold text-navy-800">
                      ${bundle.price_usb.toFixed(0)}
                    </span>
                    {usbSavings > 0 && (
                      <span className="text-sm font-semibold text-green-600">Save {usbSavings}%</span>
                    )}
                  </div>
                </div>
                <ul className="space-y-3 mt-8 text-sm text-slate-700">
                  <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" /> Everything in Download Access</li>
                  <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" /> Physical USB drive shipped to you</li>
                  <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" /> Watch offline anywhere</li>
                  <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" /> Great for team sharing</li>
                </ul>
                <Button
                  size="lg"
                  className="w-full mt-8 bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold py-6 rounded-xl"
                  onClick={() => handleAddToCart('usb')}
                  disabled={adding}
                  data-testid="add-bundle-usb-btn"
                >
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get USB Drive<ArrowRight className="ml-2 w-4 h-4" /></>}
                </Button>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-xs text-slate-500 mt-8">
            <ShieldCheck className="w-4 h-4 inline mr-1" /> Secure checkout · All major cards accepted · Instant access
          </p>
        </div>
      </section>

      {/* ================ AUDIENCE ================ */}
      {bundle.audience?.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-800 mb-3"
                  data-testid="who-is-this-for-heading">
                Who Is This For
              </h2>
              <div className="w-16 h-1 bg-gold-500 mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
              {bundle.audience.map((a, i) => (
                <div key={i} className="flex flex-col items-center text-center p-4"
                     data-testid={`audience-${i}`}>
                  <div className="text-4xl mb-3">{a.icon}</div>
                  <div className="text-sm font-medium text-navy-700">{a.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================ FINAL CTA ================ */}
      <section className="py-20 bg-[#09090b] text-white border-t border-zinc-800">
        <div className="container-custom max-w-3xl text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 leading-tight text-white"
              style={{ color: '#ffffff' }}>
            Your Team Is One Audit Away From a Very Expensive Lesson.
          </h2>
          <p className="text-slate-300 mb-10">
            Invest in compliance knowledge today. The cost of one EEOC settlement or I-9 penalty
            far exceeds the price of this entire series.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold px-8 py-6 rounded-xl"
              onClick={() => handleAddToCart('usb')}
              disabled={adding}
              data-testid="final-cta-usb"
            >
              Get USB Drive — ${bundle.price_usb.toFixed(0)}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 px-8 py-6 rounded-xl"
              onClick={() => handleAddToCart('download')}
              disabled={adding}
              data-testid="final-cta-download"
            >
              Start with Download — ${bundle.price_download.toFixed(0)}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-8">
            © GRC Educators · All sessions are pre-recorded on-demand webinars
          </p>
        </div>
      </section>
    </div>
  );
}
